import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CloudDone as CloudDoneIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { LedgerSyncData } from '../../types/franchise';

interface LedgerSyncStatusProps {
  data: LedgerSyncData;
}

export const LedgerSyncStatus: React.FC<LedgerSyncStatusProps> = ({ data }) => {
  const syncColor = data.syncPercentage >= 95 ? '#2E7D32' : data.syncPercentage >= 85 ? '#F57C00' : '#D32F2F';

  const formatTimeSince = (isoDate: string): string => {
    const diff = Date.now() - new Date(isoDate).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Ledger Sync Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Last sync: {formatTimeSince(data.lastSyncTime)}
            </Typography>
          </Box>
        </Box>

        {/* Main sync progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudDoneIcon sx={{ fontSize: 20, color: syncColor }} />
              <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: syncColor }}>
                {data.syncPercentage}%
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {data.syncedUnits}/{data.totalUnits} units synced
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={data.syncPercentage}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: `${syncColor}15`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: syncColor,
                borderRadius: 5,
              },
            }}
          />
        </Box>

        {/* Flagged units */}
        {data.flaggedUnits.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <WarningIcon sx={{ fontSize: 16, color: '#F57C00' }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#F57C00' }}>
                Flagged Units ({data.flaggedUnits.length})
              </Typography>
            </Box>
            {data.flaggedUnits.map((unit, index) => (
              <Box
                key={index}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: '#FFF3E0',
                  border: '1px solid #FFE0B2',
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    {unit.name}
                  </Typography>
                  <Chip
                    label={unit.lastSync}
                    size="small"
                    sx={{ fontSize: '0.68rem', height: 20, bgcolor: '#FFE0B2', color: '#E65100' }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {unit.reason}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
